import React, { useEffect, useRef } from "react";

/**
 * Uma carta do álbum trazida à frente, em cima de um palco, com a curiosidade dela
 * ao lado.
 *
 * POR QUE ISTO EXISTE: as cartas do encerramento cresceram de 88 para 300px e
 * continuavam MORTAS — o aluno tocava e nada acontecia, embora o texto que explica
 * cada uma já estivesse no dado (`card.pilula`, 92 a 190 caracteres já escritos e
 * aprovados, os mesmos que o mascote fala na cerimônia de revelação). Carta grande
 * com afordância de toque e sem resposta é pior que carta pequena.
 *
 * O TEXTO AQUI É DESCRIÇÃO, NÃO FALA. Na cerimônia a mesma frase sai da boca do
 * mascote, digitada caractere a caractere, porque ali é a primeira vez e o momento é
 * de revelação. Aqui o aluno está REVENDO o que já ganhou, por vontade própria e na
 * ordem que quiser: fazer o texto ser digitado de novo o obrigaria a esperar por uma
 * fala que ele já ouviu. Por isso não há bolha, não há mascote e não há cursor — é
 * legenda de museu, e aparece inteira.
 *
 * NÃO USA PORTAL, ao contrário da cerimônia. Lá o portal existe porque
 * `position: fixed` dentro de um ancestral com `transform` passa a se medir por ele.
 * Aqui isso é justamente o que se quer: o palco do encerramento tem
 * `translateZ(0)` (a regra que conserta a barra de rolagem), então o destaque cobre
 * a área da trilha e não a página inteira do AVA — nada de escurecer o menu e a barra
 * lateral por trás.
 *
 * O PALCO É `absolute`, e quem monta isto escolhe contra o que ele se mede. No
 * encerramento é a área da trilha (o palco de lá tem `translateZ(0)`); na coleção da
 * área de conquistas é a janela, porque a seção da coleção é comprida e uma carta
 * aberta no fim dela centralizaria fora da vista — lá o componente vem dentro de um
 * `.colecao__lupa`, que é `fixed`. O componente não precisa saber qual dos dois.
 *
 * @param {object}   card      { nome, asset, pilula, tipo }
 * @param {string}   [detalhe] uma linha de proveniência sob a descrição, quando
 *                            houver o que dizer — hoje só a coleção usa, com a data
 *                            de conquista. É legenda de museu, não parte do conteúdo
 *                            educativo: fica em cinza e no corpo pequeno.
 * @param {Function} onFechar
 */
function CartaEmDestaque({ card, detalhe, onFechar }) {
  const painelRef = useRef(null);

  /* O foco entra no painel ao abrir, e é o que faz o Tab circular AQUI em vez de
     continuar passeando pelas cartas atrás. Sem isto, quem navega por teclado abre o
     destaque e continua com o foco na vitrine, atrás de um véu escuro. */
  useEffect(() => {
    if (painelRef.current) painelRef.current.focus();
  }, []);

  /* `Esc` fecha o destaque, e o `stopPropagation` é o detalhe que importa: o modo
     tela cheia também escuta `Esc` para sair, e sem isto um toque na tecla faria as
     duas coisas — o aluno fecharia a carta E perderia a tela cheia. É a mesma guarda
     que `ModoTelaCheia` já aplica para os modais do AVA, aqui vista do outro lado. */
  useEffect(() => {
    function aoTeclar(evento) {
      if (evento.key !== "Escape" && evento.key !== "Esc") return;
      evento.stopPropagation();
      onFechar();
    }
    document.addEventListener("keydown", aoTeclar, true);
    return () => document.removeEventListener("keydown", aoTeclar, true);
  }, [onFechar]);

  return (
    /* O PALCO. Clicar nele fecha — é o gesto que todo mundo já espera de uma imagem
       aberta em cima de um fundo escuro, e perdoa o dedo impreciso de uma criança de
       6 anos, que é o mesmo argumento do painel da cerimônia.

       Sem `role`/`onKeyDown` neste div de propósito: ele é uma área de toque
       REDUNDANTE. O caminho acessível é o botão de fechar e a tecla Esc. */
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <div
      className="encerramento__palco tl-anim-fade-in"
      onClick={onFechar}
      data-testid="encerramento-palco"
    >
      {/* O clique PARA aqui: dentro do painel, tocar na carta ou no texto não pode
          fechar, senão selecionar uma palavra da descrição derruba a tela. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={`encerramento__destaque tl-anim-pop-in${
          card.tipo === "raro" ? " encerramento__destaque--raro" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={`Carta ${card.nome}`}
        tabIndex={-1}
        ref={painelRef}
        onClick={evento => evento.stopPropagation()}
      >
        <div className="encerramento__destaque-carta">
          <img
            src={card.asset}
            alt=""
            width="420"
            height="588"
            draggable={false}
          />
        </div>

        <div className="encerramento__destaque-texto">
          <h3 className="encerramento__destaque-nome">{card.nome}</h3>
          {card.pilula && (
            <p className="encerramento__destaque-descricao">{card.pilula}</p>
          )}
          {detalhe && (
            <p className="encerramento__destaque-detalhe">{detalhe}</p>
          )}
        </div>

        <button
          type="button"
          className="encerramento__destaque-fechar"
          onClick={onFechar}
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export default CartaEmDestaque;
